import { createAsyncThunk } from '@reduxjs/toolkit';
import { enabledNewPromotion } from '@castlery/config';
import type { ExtraArgument } from '@castlery/shared-redux-extra';
const autoCouponMarked = (orderNumber: string, couponCode: string) => {
  return `${orderNumber}_${couponCode}`;
};

// order domain (v1)
import {
  selectCurrentOrderNumber,
  getCouponsByOrderNumberV1,
  applyCouponV1,
  removeCouponV1,
  setCoupons as setCouponsV1,
  getGiftsByOrderNumberV1,
  type CouponItemV1,
} from '@castlery/modules-order-domain';

// promotion domain (v2)
import {
  getCouponsByOrderNumberV2,
  applyCouponV2,
  removeCouponV2,
  setCouponsV2,
  getGiftsByOrderNumberSilent,
  addGiftsByOrderNumberV2,
  type CouponItemV2,
  getGiftsByOrderNumberV2,
} from '@castlery/modules-promotion-domain';

function resolveOrderNumber(getState: any, extra: unknown): string | undefined {
  const fromState = selectCurrentOrderNumber(getState());
  if (fromState) return fromState as string;
  const { persistenceHandles } = (extra || {}) as ExtraArgument;
  const fromExtra = persistenceHandles?.orderNumber?.getItem?.();
  return (fromExtra as string) || undefined;
}

export type PromotionCoupons = CouponItemV1[] | CouponItemV2[];

export const fetchCouponsCommand = createAsyncThunk<PromotionCoupons>(
  'promotion/fetchCoupons',
  async (_: void, { dispatch, getState, rejectWithValue, extra }) => {
    const orderNumber = resolveOrderNumber(getState, extra);
    if (!orderNumber) {
      return rejectWithValue('[fetchCouponsCommand]: order not found');
    }
    if (enabledNewPromotion) {
      const res: any = await dispatch(getCouponsByOrderNumberV2.initiate(orderNumber));
      if ('error' in res) return rejectWithValue(res.error);
      if (res?.data) dispatch(setCouponsV2(res.data));
      return res.data as CouponItemV2[];
    }

    const res: any = await dispatch(getCouponsByOrderNumberV1.initiate(orderNumber));
    if ('error' in res) return rejectWithValue(res.error);
    if (res?.data) dispatch(setCouponsV1(res.data));
    return res.data as CouponItemV1[];
  }
);

export const applyCouponCommand = createAsyncThunk(
  'promotion/applyCoupon',
  async ({ couponCode }: { couponCode: string }, { dispatch, getState, rejectWithValue, extra }) => {
    const orderNumber = resolveOrderNumber(getState, extra);
    if (!orderNumber) {
      return rejectWithValue('[applyCouponCommand]: order not found');
    }
    let res: any;
    if (enabledNewPromotion) {
      res = await dispatch(applyCouponV2.initiate({ orderNumber, couponCode }));
    } else {
      res = await dispatch(applyCouponV1.initiate({ number: orderNumber, couponCode }));
    }
    if ('error' in res) return rejectWithValue(res.error);
    await dispatch(fetchCouponsCommand());
    return res.data;
  }
);

export const removeCouponCommand = createAsyncThunk(
  'promotion/removeCoupon',
  async ({ couponCode }: { couponCode: string }, { dispatch, getState, rejectWithValue, extra }) => {
    const orderNumber = resolveOrderNumber(getState, extra);
    if (!orderNumber) {
      return rejectWithValue('[removeCouponCommand]: order not found');
    }
    let res: any;

    if (enabledNewPromotion) {
      res = await dispatch(removeCouponV2.initiate({ orderNumber, couponCode }));
    } else {
      res = await dispatch(removeCouponV1.initiate({ number: orderNumber, couponCode }));
    }
    if ('error' in res) return rejectWithValue(res.error);

    const { persistenceHandles } = extra as ExtraArgument;
    const markedSymbol = autoCouponMarked(orderNumber, couponCode);
    persistenceHandles.autoAppliedCoupon.setItem(markedSymbol);
    return res.data;
  }
);

export const fetchGiftsCommand = createAsyncThunk(
  'promotion/fetchGifts',
  async (
    { stockLocationId, couponCode }: { stockLocationId?: string; couponCode?: string },
    { dispatch, getState, rejectWithValue, extra }
  ) => {
    const orderNumber = resolveOrderNumber(getState, extra);
    if (!orderNumber) {
      return rejectWithValue('[fetchGiftsCommand]: order not found');
    }

    if (!enabledNewPromotion) {
      return rejectWithValue('[fetchGiftsCommand]: not supported in promotion v1');
    }

    const res: any = await dispatch(
      getGiftsByOrderNumberV2.initiate({ orderNumber, stockLocationId, coupon_code: couponCode })
    );
    if ('error' in res) return rejectWithValue(res.error);
    return res.data;
  }
);

/**
 *  v1 没有gift 所以不需要区分这个 api
 */
// export const addGiftCommand = createAsyncThunk(
//   'promotion/addGift',
//   async (
//     { giftId, variantId, couponCode }: { giftId: string; variantId: number; couponCode?: string },
//     { dispatch, getState, rejectWithValue, extra }
//   ) => {
//     const orderNumber = resolveOrderNumber(getState, extra);
//     if (!orderNumber) {
//       return rejectWithValue('[addGiftCommand]: order not found');
//     }

//     if (!ecPosFeatures.enabledNewPromotion ) {
//       return rejectWithValue('[addGiftCommand]: not supported in promotion v1');
//     }

//     const res: any = await dispatch(
//       addGiftV2.initiate({ orderNumber, gift_id: giftId, variant_id: variantId, coupon_code: couponCode })
//     );
//     if ('error' in res) return rejectWithValue(res.error);

//     // 刷新 gifts 列表
//     await dispatch(fetchGiftsCommand({ couponCode }));
//     return res.data;
//   }
// );
